/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {getPayload} from 'payload';

import {SeoHead} from 'UI/components/layout/SeoHead';
import {MapCategoryFilter} from 'UI/components/modules/MapCategoryFilter';
import {MapWalkSlider} from 'UI/components/modules/MapWalkSlider';
import {NearbyWaypoints} from 'UI/components/modules/NearbyWaypoints';
import {WalkMap} from 'UI/components/modules/WalkMap';
import type {FitPadding, FountainMarker} from 'UI/components/modules/WalkMap';

import payloadConfig from '@payload-config';
import {DEFAULT_LOCALE} from 'GlobalShared/locales';
import {useKartePage} from 'UI/hooks/useKartePage';
import {AppLayout} from 'UI/layouts/AppLayout';

import {getDrinkingFountains} from 'Payload/utils/getDrinkingFountains';
import {getLegalPages} from 'Payload/utils/getLegalPages';

import type {GetStaticProps, InferGetStaticPropsType} from 'next';
import type {Payload} from 'payload';
import type {NextSSGPageWithLayout} from 'types/global';
import type {Category, Config, LegalPage, Walk, Waypoint} from 'types/payload-types';
import type {KarteWalk, KarteWaypoint} from 'UI/hooks/useKartePage';

interface KartePageProps {
    categories: Pick<Category, 'id' | 'name' | 'predefinedIcon'>[];
    fountains: FountainMarker[];
    legalPages: LegalPage[];
    walks: KarteWalk[];
}

/** Reserves space above (category filter bar) and below (walk slider) the markers so they never load hidden underneath either overlay. */
const KARTE_FIT_PADDING: FitPadding = {
    bottom: 280,
    top: 100
};

/**
 * Karten-Übersichtsseite. Zeigt alle veröffentlichten Walks mit Wegpunkten auf einer Karte,
 * gebrowst über einen horizontalen Walk-Slider und einen Kategorie-Filter.
 *
 * @param props            Die Seiten-Props.
 * @param props.categories Array aller Kategorien für den Filter.
 * @param props.fountains  Array aller Trinkwasser-Stellen für die Kartenanzeige.
 * @param props.walks      Array aller veröffentlichten Walks mit Wegpunkten.
 * @returns ReactNode der Karten-Übersichtsseite.
 */
const KartePage: NextSSGPageWithLayout<typeof getStaticProps> = (
    {categories, fountains, walks}: InferGetStaticPropsType<typeof getStaticProps>
) => {
    const {
        activeMarker,
        filterOptions,
        handleCloseWaypoint,
        handleSelectWalk,
        mapSelectedWalkIds,
        markers,
        selectedCategoryIds,
        selectedWalkId,
        setActiveMarker,
        sliderCards,
        t,
        toggleCategory
    } = useKartePage(walks, categories);

    return (
        <Content data-cy="KartePage">
            <SeoHead
                description={t('karte.metaDescription')}
                title={t('karte.title')}
                url={`${process.env.NEXT_PUBLIC_BASE_URL}/map`}
            />
            <WalkMap
                fitPadding={KARTE_FIT_PADDING}
                fountains={fountains}
                height="calc(100dvh - 6.4rem - 6.4rem)"
                markers={markers}
                selectedWalkIds={mapSelectedWalkIds}
                testId="KartePage-map"
                variant="full"
                onActiveMarkerChange={setActiveMarker}
            >
                <MapCategoryFilter
                    categories={filterOptions}
                    selectedCategoryIds={selectedCategoryIds}
                    testId="KartePage-filter"
                    onToggle={toggleCategory}
                />
                {sliderCards.length > 0
                    ? (
                        <MapWalkSlider
                            activeWaypoint={activeMarker}
                            selectedWalkId={selectedWalkId}
                            testId="KartePage-slider"
                            walks={sliderCards}
                            onCloseWaypoint={handleCloseWaypoint}
                            onSelectWalk={handleSelectWalk}
                        />
                    )
                    : <NoResults>{t('walks.noResults')}</NoResults>}
            </WalkMap>
            <NearbyWaypointsSection>
                <NearbyWaypoints />
            </NearbyWaypointsSection>
        </Content>
    );
};

/**
 * `getLayout` wraps die Seite mit dem App-Shell-Layout (Header + Navigation).
 *
 * @param _route        Die aktuelle Route.
 * @param pageProps     Die Seiten-Props.
 * @param PageComponent Die Seitenkomponente.
 * @returns ReactNode mit AppLayout.
 */
KartePage.getLayout = (_route, pageProps, PageComponent) => (
    <AppLayout activeTab="map" legalPages={pageProps.legalPages} isBackVisible>
        <PageComponent {...pageProps} />
    </AppLayout>
);

/**
 * `getLayoutKey` gibt den Layout-Schlüssel zurück.
 *
 * @returns Der Layout-Key-String.
 */
KartePage.getLayoutKey = () => 'map';

export default KartePage;

// ── Styled ──────────────────────────────────────────────────

const Content = styled.div`
    position: relative;
`;

const NoResults = styled.p`
    background: ${({theme}) => theme.colors.white};
    border-radius: 9999px;
    bottom: 2.4rem;
    box-shadow: ${({theme}) => theme.boxShadows.floatingShadow};
    color: ${({theme}) => theme.colors.inkTertiary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    left: 50%;
    padding: 1.2rem 2.4rem;
    position: absolute;
    transform: translateX(-50%);
    z-index: 950;
`;

const NearbyWaypointsSection = styled.div`
    padding: 0 ${spacing(6)};
`;

/**
 * Lädt alle veröffentlichten Walks (mit Wegpunkten) sowie alle Kategorien für den Filter.
 *
 * @param props                  Die Static-Props Kontext-Optionen.
 * @param props.locale           Die aktuelle Locale.
 * @param props.revalidateReason Der Grund für die Revalidierung.
 * @returns Props mit Walks-, Kategorie- und Legal-Pages-Arrays.
 */
export const getStaticProps: GetStaticProps<KartePageProps> = async ({locale, revalidateReason}) => {
    let payload: Payload | undefined;

    if (revalidateReason !== 'build') {
        payload = await getPayload({config: payloadConfig});
    }

    if (!payload) {
        return {
            props: {
                categories: [],
                fountains: [],
                legalPages: [],
                walks: []
            },
            revalidate: 1
        };
    }

    const payloadLocale = (locale ?? DEFAULT_LOCALE) as Config['locale'];

    const [walksResult, categoriesResult, legalPages, fountains] = await Promise.all([
        payload.find({
            collection: 'walks',
            depth: 1,
            joins: {waypoints: false},
            limit: 1000,
            locale: payloadLocale,
            select: {
                categories: true,
                city: true,
                coverImage: true,
                pricing: true,
                shortDescription: true,
                slug: true,
                title: true
            },
            where: {_status: {equals: 'published'}}
        }),
        payload.find({
            collection: 'categories',
            depth: 0,
            limit: 100,
            select: {
                name: true,
                predefinedIcon: true
            }
        }),
        getLegalPages(payload, payloadLocale),
        getDrinkingFountains(payload)
    ]);

    const walkDocs = walksResult.docs as unknown as Walk[];
    const walkIds = walkDocs.map(w => w.id);

    const waypointsResult = walkIds.length > 0
        ? await payload.find({
            collection: 'waypoints',
            depth: 0,
            limit: 0,
            locale: payloadLocale,
            select: {
                location: true,
                shortDescription: true,
                sortOrder: true,
                title: true,
                walk: true
            },
            sort: 'sortOrder',
            where: {walk: {in: walkIds}}
        })
        : null;

    const waypointsByWalkId: Record<string, KarteWaypoint[]> = {};

    for (const wp of (waypointsResult?.docs ?? []) as unknown as Waypoint[]) {
        if (!wp.location) continue;

        const walkId = typeof wp.walk === 'object' ? wp.walk.id : wp.walk;

        (waypointsByWalkId[walkId] ??= []).push({
            coordinates: wp.location,
            description: wp.shortDescription ?? undefined,
            id: wp.id,
            title: wp.title
        });
    }

    const walks: KarteWalk[] = walkDocs
        .map((walk): KarteWalk => {
            const coverImage = typeof walk.coverImage === 'object' ? walk.coverImage : null;
            const city = typeof walk.city === 'object' ? walk.city.name : '';
            const categories = (walk.categories ?? [])
                .filter((c): c is Category => typeof c === 'object')
                .map(c => ({
                    id: c.id,
                    name: c.name
                }));

            return {
                categories,
                city,
                id: walk.id,
                imageAlt: coverImage?.alt ?? undefined,
                imageUrl: coverImage?.sizes?.card?.url
                    ?? coverImage?.sizes?.thumbnail?.url
                    ?? coverImage?.url
                    ?? undefined,
                isFree: walk.pricing?.isFree !== false,
                price: walk.pricing?.price ?? undefined,
                shortDescription: walk.shortDescription,
                slug: walk.slug,
                title: walk.title,
                waypoints: waypointsByWalkId[walk.id] ?? []
            };
        })
        .filter(walk => walk.waypoints.length > 0);

    return {
        props: {
            categories: JSON.parse(JSON.stringify(categoriesResult.docs)) as KartePageProps['categories'],
            fountains,
            legalPages,
            walks: JSON.parse(JSON.stringify(walks)) as KarteWalk[]
        },
        revalidate: 18000
    };
};